import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  NoSymbolIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ALL_AGENTS = 'all';

const PRESETS = [
  { key: '6h', label: 'Last 6 hours', hours: 6, granularity: 'hour' },
  { key: '24h', label: 'Last 24 hours', hours: 24, granularity: 'hour' },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7, granularity: 'day' },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30, granularity: 'day' },
  { key: '12w', label: 'Last 12 weeks', hours: 24 * 7 * 12, granularity: 'week' }
];

const GRANULARITY_OPTIONS = [
  { key: 'hour', label: 'Per hour' },
  { key: 'day', label: 'Per day' },
  { key: 'week', label: 'Per week' }
];

const SCAN_FILTERS = [
  { key: 'all', label: 'All scans' },
  { key: 'never', label: 'Never scanned' },
  { key: 'phase1', label: 'Phase 1 only' },
  { key: 'phase2', label: 'Phase 2 (full scrape)' },
  { key: 'recent', label: 'Recent (< 6h)' },
  { key: 'stale', label: 'Stale / never' },
  { key: 'error', label: 'Has errors' },
  { key: 'excluded', label: 'Excluded' },
  { key: 'ledger_gap', label: 'Ledger gap (≠ 0)' }
];

const FOLLOWBACK_FILTERS = [
  { key: 'all', label: 'All rates' },
  { key: 'high', label: 'High follow-back' },
  { key: 'low', label: 'Low follow-back' },
  { key: 'none', label: 'No follow-backs' }
];

const SORT_OPTIONS = [
  { key: 'last_scan_desc', label: 'Latest scan first' },
  { key: 'last_scan_asc', label: 'Oldest scan first' },
  { key: 'followback_pct_desc', label: 'Highest follow-back %' },
  { key: 'followback_pct_asc', label: 'Lowest follow-back %' },
  { key: 'pending_desc', label: 'Most pending checks' },
  { key: 'discrepancy_desc', label: 'Largest ledger gap' }
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const timeAgo = (value) => {
  if (!value) return 'never';
  const ms = Date.now() - new Date(value).getTime();
  if (Number.isNaN(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const phaseBadge = (phase, success) => {
  if (phase === 'phase2') {
    return success === false
      ? { label: 'Phase 2 (failed)', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' }
      : { label: 'Phase 2', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' };
  }
  if (phase === 'phase1') {
    return { label: 'Phase 1 only', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' };
  }
  return { label: 'Not scanned', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' };
};

const FollowbackHealthTab = () => {
  const { isDarkMode } = useTheme();

  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(ALL_AGENTS);
  const [activePreset, setActivePreset] = useState('7d');
  const [customRange, setCustomRange] = useState([null, null]);
  const [granularity, setGranularity] = useState('day');
  const [overview, setOverview] = useState(null);
  const [accountsData, setAccountsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [scanFilter, setScanFilter] = useState('all');
  const [followBackFilter, setFollowBackFilter] = useState('all');
  const [sort, setSort] = useState('last_scan_desc');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [customStart, customEnd] = customRange;

  const range = useMemo(() => {
    if (activePreset === 'custom' && customStart && customEnd) {
      return { from: customStart.toISOString(), to: customEnd.toISOString() };
    }
    const preset = PRESETS.find((p) => p.key === activePreset) || PRESETS[2];
    return {
      from: new Date(Date.now() - preset.hours * 3600 * 1000).toISOString(),
      to: new Date().toISOString()
    };
  }, [activePreset, customStart, customEnd]);

  const isAllAgents = selectedAgentId === ALL_AGENTS;

  const fetchAgents = useCallback(async () => {
    const res = await api.get('/followback-health/agents', { params: { lite: 1 } });
    const list = res.data.agents || [];
    setAgents(list);
  }, []);

  const fetchOverview = useCallback(async () => {
    const params = {
      from: range.from,
      to: range.to,
      granularity,
      ...(isAllAgents ? {} : { agentId: selectedAgentId })
    };
    const res = await api.get('/followback-health/overview', { params });
    setOverview(res.data);
  }, [range.from, range.to, granularity, selectedAgentId, isAllAgents]);

  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const res = await api.get('/followback-health/accounts', {
        params: {
          agentId: isAllAgents ? ALL_AGENTS : selectedAgentId,
          scanFilter,
          followBackFilter,
          sort,
          search: accountSearch.trim() || undefined,
          page,
          limit: 50
        }
      });
      setAccountsData(res.data);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error fetching followback accounts:', err);
      setError(err.response?.data?.error || 'Failed to load accounts');
    } finally {
      setAccountsLoading(false);
    }
  }, [selectedAgentId, isAllAgents, scanFilter, followBackFilter, sort, accountSearch, page]);

  const refreshAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await fetchAgents();
      await Promise.all([fetchOverview(), fetchAccounts()]);
    } catch (err) {
      console.error('Error fetching followback health:', err);
      setError(err.response?.data?.error || 'Failed to load followback health data');
    } finally {
      setLoading(false);
    }
  }, [fetchAgents, fetchOverview, fetchAccounts]);

  useEffect(() => {
    fetchAgents().catch(() => {});
  }, [fetchAgents]);

  useEffect(() => {
    fetchOverview().catch(() => {});
  }, [fetchOverview]);

  useEffect(() => {
    fetchAccounts().catch(() => {});
  }, [fetchAccounts]);

  const selectPreset = (preset) => {
    setActivePreset(preset.key);
    setGranularity(preset.granularity);
  };

  const runAction = async (action) => {
    if (selectedIds.size === 0) return;
    const label = action === 'eject' ? 'eject from follow automation'
      : action === 'exclude' ? 'exclude from follow-back scanning'
      : action === 'include' ? 're-include in follow-back scanning'
      : 'eject and exclude';
    if (!window.confirm(`Confirm: ${label} for ${selectedIds.size} account(s)?`)) return;

    const rowsById = new Map((accountsData?.accounts || []).map((a) => [a.accountId, a]));
    const byAgent = new Map();
    for (const accountId of selectedIds) {
      const row = rowsById.get(accountId);
      const agentId = row?.agentId || (isAllAgents ? null : selectedAgentId);
      if (!agentId) continue;
      if (!byAgent.has(agentId)) byAgent.set(agentId, []);
      byAgent.get(agentId).push(accountId);
    }
    if (!byAgent.size) {
      setError('Could not resolve agent for selected account(s)');
      return;
    }

    try {
      setActionLoading(true);
      await Promise.all(
        Array.from(byAgent.entries()).map(([agentId, accountIds]) =>
          api.post('/followback-health/accounts/action', { agentId, accountIds, action })
        )
      );
      await Promise.all([fetchOverview(), fetchAccounts()]);
    } catch (err) {
      setError(err.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const rows = accountsData?.accounts || [];
    if (selectedIds.size === rows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((r) => r.accountId)));
    }
  };

  const gridColor = isDarkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(100, 116, 139, 0.15)';
  const tickColor = isDarkMode ? '#94a3b8' : '#64748b';

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { color: tickColor, usePointStyle: true, boxWidth: 8 } }
    },
    scales: {
      x: { grid: { color: gridColor }, ticks: { color: tickColor, maxRotation: 45 } },
      y: { beginAtZero: true, grid: { color: gridColor }, ticks: { color: tickColor, precision: 0 } }
    }
  }), [gridColor, tickColor]);

  const labels = useMemo(
    () => overview?.scans?.series?.map((p) => p.bucket) || [],
    [overview]
  );

  const scansChart = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'Successful scans',
        data: overview?.scans?.series?.map((p) => p.success) || [],
        backgroundColor: 'rgba(14, 165, 233, 0.7)',
        borderRadius: 4
      },
      {
        label: 'Failed scans',
        data: overview?.scans?.series?.map((p) => p.failed) || [],
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderRadius: 4
      }
    ]
  }), [overview, labels]);

  const dmsChart = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'DMs sent',
        data: overview?.dms?.series?.map((p) => p.sent) || [],
        backgroundColor: 'rgba(168, 85, 247, 0.7)',
        borderRadius: 4
      },
      {
        label: 'DM failures',
        data: overview?.dms?.series?.map((p) => p.failed) || [],
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderRadius: 4
      },
      {
        label: 'Follow-backs detected',
        data: overview?.followBacksDetected?.series?.map((p) => p.count) || [],
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderRadius: 4
      }
    ]
  }), [overview, labels]);

  const selectedAgent = isAllAgents ? null : agents.find((a) => a.agentId === selectedAgentId);
  const summary = overview?.healthSummary || {};
  const ledgerRec = overview?.ledgerReconciliation || {};
  const activeScanners = isAllAgents
    ? agents.filter((a) => a.scanner?.active).length
    : (selectedAgent?.scanner?.active ? 1 : 0);

  const statCards = [
    {
      label: 'Follow-back scans',
      value: overview?.scans?.totals?.success ?? 0,
      sub: `${overview?.scans?.totals?.failed ?? 0} failed in range`,
      icon: MagnifyingGlassIcon,
      color: 'text-sky-500'
    },
    {
      label: 'Follow-back DMs',
      value: overview?.dms?.totals?.sent ?? 0,
      sub: `${overview?.dms?.totals?.failed ?? 0} failed · ${overview?.followBacksDetected?.total ?? 0} detected`,
      icon: UserPlusIcon,
      color: 'text-purple-500'
    },
    {
      label: 'Ledger vs following',
      value: ledgerRec.discrepancyTotal != null
        ? `${ledgerRec.discrepancyTotal >= 0 ? '+' : ''}${ledgerRec.discrepancyTotal.toLocaleString()}`
        : '—',
      sub: (ledgerRec.accountsTotal ?? 0) === 0
        ? (ledgerRec.accountsAttached ?? 0) > 0
          ? `No Phase 1 scan data yet · ${(ledgerRec.accountsAttached ?? 0).toLocaleString()} attached — run a followback test or start the scanner`
          : 'No attached accounts for this selection'
        : `${(ledgerRec.profileFollowingTotal ?? 0).toLocaleString()} on X · ${(ledgerRec.ledgerFollowedTotal ?? 0).toLocaleString()} in ledger · ${ledgerRec.accountsWithGap ?? 0} gap · ${ledgerRec.accountsTotal.toLocaleString()} P1-scanned`,
      icon: ShieldCheckIcon,
      color: Math.abs(ledgerRec.discrepancyTotal || 0) > 0 ? 'text-amber-500' : 'text-emerald-500'
    },
    {
      label: 'With errors',
      value: summary.withErrors ?? 0,
      sub: `${summary.excluded ?? 0} excluded · ${summary.suspended ?? 0} suspended/auth`,
      icon: ExclamationTriangleIcon,
      color: 'text-red-500'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Agent + filters */}
      <div className="card overflow-visible">
        <div className="card-body overflow-visible space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                VA Agent
              </label>
              <select
                value={selectedAgentId}
                onChange={(e) => { setSelectedAgentId(e.target.value); setPage(1); }}
                className={`px-3 py-1.5 text-sm rounded-lg border min-w-[220px] ${
                  isDarkMode
                    ? 'bg-slate-800 text-slate-200 border-slate-700'
                    : 'bg-white text-slate-700 border-slate-200'
                }`}
              >
                <option value={ALL_AGENTS}>
                  All agents ({agents.reduce((n, a) => n + (a.accountCount || 0), 0)} logged-in)
                </option>
                {agents.map((a) => (
                  <option key={a.agentId} value={a.agentId}>
                    {a.name} ({a.accountCount} logged-in)
                  </option>
                ))}
              </select>
              {isAllAgents ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {activeScanners} active scanner(s) · aggregated view
                </p>
              ) : selectedAgent && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Scanner {selectedAgent.scanner?.active ? 'active' : 'inactive'}
                  {selectedAgent.scanner?.lastScanAt ? ` · last pass ${timeAgo(selectedAgent.scanner.lastScanAt)}` : ''}
                  {selectedAgent.scanner?.scanning ? ' · scanning now' : ''}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Time range
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    onClick={() => selectPreset(preset)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      activePreset === preset.key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : isDarkMode
                          ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom range
              </label>
              <DatePicker
                selectsRange
                startDate={customStart}
                endDate={customEnd}
                onChange={(update) => {
                  setCustomRange(update);
                  if (update[0] && update[1]) setActivePreset('custom');
                }}
                showTimeSelect
                timeIntervals={60}
                dateFormat="MMM d, yyyy HH:mm"
                maxDate={new Date()}
                isClearable
                placeholderText="Pick start & end…"
                className={`px-3 py-1.5 text-sm rounded-lg border w-64 ${
                  isDarkMode
                    ? 'bg-slate-800 text-slate-200 border-slate-700'
                    : 'bg-white text-slate-700 border-slate-200'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Group by
              </label>
              <div className="flex gap-2">
                {GRANULARITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setGranularity(opt.key)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      granularity === opt.key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : isDarkMode
                          ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={refreshAll}
              disabled={loading}
              className={`ml-auto flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                isDarkMode
                  ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              } ${loading ? 'opacity-60' : ''}`}
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                    {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{card.sub}</p>
                </div>
                <card.icon className={`w-8 h-8 ${card.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Ledger reconciliation */}
      {ledgerRec.accountsTotal > 0 && (
        <div className="card">
          <div className="card-body">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Ledger vs X following
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Uses latest followback Phase 1 measurements ({ledgerRec.dataSource === 'last_test' ? 'last test run' : 'per-account P1 stats'}) for following on X, compared against automation ledger rows recorded as followed.
              Only accounts that have been Phase 1 scanned are included in the totals.
              A positive gap usually means follows not yet written to the ledger or pre-automation follows on the account.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Following on X (total)</p>
                <p className="text-xl font-semibold text-gray-900 dark:text-white">
                  {(ledgerRec.profileFollowingTotal ?? 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Ledger followed (total)</p>
                <p className="text-xl font-semibold text-gray-900 dark:text-white">
                  {(ledgerRec.ledgerFollowedTotal ?? 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Net discrepancy</p>
                <p className={`text-xl font-semibold ${
                  (ledgerRec.discrepancyTotal || 0) === 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`}>
                  {(ledgerRec.discrepancyTotal ?? 0) >= 0 ? '+' : ''}
                  {(ledgerRec.discrepancyTotal ?? 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Accounts with gap</p>
                <p className="text-xl font-semibold text-gray-900 dark:text-white">
                  {ledgerRec.accountsWithGap ?? 0}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    {' '}/ {ledgerRec.accountsTotal} P1 · {ledgerRec.accountsAttached ?? ledgerRec.accountsTotal} attached
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-body">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Follow-back scans
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Phase 2 follower scrapes per {granularity}
            </p>
            <div className="h-64">
              <Bar data={scansChart} options={chartOptions} />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Follow-backs &amp; DMs
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Detected follow-backs and DMs per {granularity}
            </p>
            <div className="h-64">
              <Bar data={dmsChart} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Recent errors */}
      {overview?.recentErrors?.length > 0 && (
        <div className="card">
          <div className="card-body">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Recent scan / DM errors
            </h3>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {overview.recentErrors.map((e, i) => (
                <div
                  key={i}
                  className={`text-xs px-3 py-2 rounded-lg ${
                    isDarkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'
                  }`}
                >
                  <span className="font-mono">{formatDateTime(e.at)}</span>
                  {e.username ? ` · @${e.username}` : ''}
                  {` · ${e.type}`} — {e.error || 'unknown error'}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Accounts table */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Account follow-back health
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {accountsData?.totals?.inAutomation ?? 0} in follow automation ·{' '}
                {accountsData?.totals?.ejected ?? 0} ejected ·{' '}
                {accountsData?.totals?.pendingChecks ?? 0} pending ledger checks ·{' '}
                {accountsData?.totals?.accountsWithLedgerGap ?? 0} with ledger gap
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={accountSearch}
                onChange={(e) => { setAccountSearch(e.target.value); setPage(1); }}
                placeholder="Search username…"
                className={`px-3 py-1.5 text-sm rounded-lg border w-40 ${
                  isDarkMode
                    ? 'bg-slate-800 text-slate-200 border-slate-700 placeholder-slate-500'
                    : 'bg-white text-slate-700 border-slate-200 placeholder-slate-400'
                }`}
              />
              <select
                value={scanFilter}
                onChange={(e) => { setScanFilter(e.target.value); setPage(1); }}
                className={`px-2 py-1.5 text-sm rounded-lg border ${
                  isDarkMode ? 'bg-slate-800 text-slate-200 border-slate-700' : 'bg-white border-slate-200'
                }`}
              >
                {SCAN_FILTERS.map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
              <select
                value={followBackFilter}
                onChange={(e) => { setFollowBackFilter(e.target.value); setPage(1); }}
                className={`px-2 py-1.5 text-sm rounded-lg border ${
                  isDarkMode ? 'bg-slate-800 text-slate-200 border-slate-700' : 'bg-white border-slate-200'
                }`}
              >
                {FOLLOWBACK_FILTERS.map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
              <select
                value={sort}
                onChange={(e) => { setSort(e.target.value); setPage(1); }}
                className={`px-2 py-1.5 text-sm rounded-lg border ${
                  isDarkMode ? 'bg-slate-800 text-slate-200 border-slate-700' : 'bg-white border-slate-200'
                }`}
              >
                {SORT_OPTIONS.map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedIds.size > 0 && (
            <div className={`flex flex-wrap items-center gap-2 mb-4 p-3 rounded-lg ${
              isDarkMode ? 'bg-slate-800' : 'bg-slate-50'
            }`}>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {selectedIds.size} selected
              </span>
              <button
                disabled={actionLoading}
                onClick={() => runAction('eject')}
                className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Eject from follow
              </button>
              <button
                disabled={actionLoading}
                onClick={() => runAction('exclude')}
                className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                <NoSymbolIcon className="w-4 h-4" />
                Exclude from scanning
              </button>
              <button
                disabled={actionLoading}
                onClick={() => runAction('eject_and_exclude')}
                className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-red-800 text-white hover:bg-red-900 disabled:opacity-50"
              >
                Both
              </button>
              <button
                disabled={actionLoading}
                onClick={() => runAction('include')}
                className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg border border-emerald-600 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50"
              >
                Re-include
              </button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className={`text-left ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <th className="py-2 pr-2 w-8">
                    <input
                      type="checkbox"
                      checked={
                        (accountsData?.accounts?.length || 0) > 0
                        && selectedIds.size === (accountsData?.accounts?.length || 0)
                      }
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="py-2 pr-4 font-medium">Account</th>
                  {isAllAgents && <th className="py-2 pr-4 font-medium">Agent</th>}
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Latest scan</th>
                  <th className="py-2 pr-4 font-medium">Scan phase</th>
                  <th className="py-2 pr-4 font-medium text-right">Followers</th>
                  <th className="py-2 pr-4 font-medium text-right" title="Following count on X">Following (X)</th>
                  <th className="py-2 pr-4 font-medium text-right" title="Automation ledger rows">Ledger</th>
                  <th className="py-2 pr-4 font-medium text-right" title="Following (X) minus ledger">Δ</th>
                  <th className="py-2 pr-4 font-medium text-right">Follow-backs</th>
                  <th className="py-2 pr-4 font-medium text-right">Pending</th>
                  <th className="py-2 pr-4 font-medium">Error</th>
                </tr>
              </thead>
              <tbody className={isDarkMode ? 'divide-y divide-slate-800' : 'divide-y divide-slate-100'}>
                {(accountsData?.accounts || []).map((a) => {
                  const badge = phaseBadge(
                    a.followBackHealth.lastScanPhase,
                    a.followBackHealth.lastPhase2Success
                  );
                  return (
                    <tr key={a.accountId} className={a.followBackHealth.excluded ? 'opacity-60' : ''}>
                      <td className="py-2.5 pr-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(a.accountId)}
                          onChange={() => toggleSelect(a.accountId)}
                        />
                      </td>
                      <td className="py-2.5 pr-4">
                        <div className="font-medium text-gray-900 dark:text-white">@{a.username}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {a.inFollowAutomation ? 'in follow job' : 'ejected'}
                          {a.followBackHealth.excluded ? ' · excluded' : ''}
                        </div>
                      </td>
                      {isAllAgents && (
                        <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-300 text-xs">
                          {a.agentName || '—'}
                        </td>
                      )}
                      <td className="py-2.5 pr-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          a.status === 'active'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        }`}>
                          {a.status}
                          {a.linkedAccountStatus && a.linkedAccountStatus !== 'active'
                            ? ` / ${a.linkedAccountStatus}` : ''}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-300" title={formatDateTime(a.followBackHealth.lastScanAt)}>
                        {timeAgo(a.followBackHealth.lastScanAt)}
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 text-right text-gray-900 dark:text-white">
                        {(a.followersCount || 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4 text-right text-gray-900 dark:text-white">
                        {(a.followingCount || 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4 text-right text-gray-900 dark:text-white">
                        {(a.ledger.totalFollowed || 0).toLocaleString()}
                      </td>
                      <td className={`py-2.5 pr-4 text-right font-medium ${
                        (a.ledger.discrepancy || 0) === 0
                          ? 'text-gray-500 dark:text-gray-400'
                          : (a.ledger.discrepancy || 0) > 0
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-red-600 dark:text-red-400'
                      }`}>
                        {(a.ledger.discrepancy ?? 0) >= 0 ? '+' : ''}
                        {(a.ledger.discrepancy ?? 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4 text-right">
                        <span className="text-gray-900 dark:text-white">
                          {(a.ledger.totalFollowBacks || 0).toLocaleString()}
                        </span>
                        {a.ledger.followBackPct != null && (
                          <span className="block text-xs text-gray-500 dark:text-gray-400">
                            {a.ledger.followBackPct}%
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-right text-gray-900 dark:text-white">
                        {(a.ledger.pendingChecks || 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4 max-w-[180px] truncate text-xs text-red-600 dark:text-red-400" title={a.followBackHealth.lastError || a.lastScanError}>
                        {a.followBackHealth.lastError || a.lastScanError || '—'}
                      </td>
                    </tr>
                  );
                })}
                {!accountsLoading && (accountsData?.accounts || []).length === 0 && (
                  <tr>
                    <td colSpan={isAllAgents ? 13 : 12} className="py-8 text-center text-gray-500 dark:text-gray-400">
                      No accounts match this filter.
                    </td>
                  </tr>
                )}
                {accountsLoading && (
                  <tr>
                    <td colSpan={isAllAgents ? 13 : 12} className="py-8 text-center text-gray-500 dark:text-gray-400">
                      Loading accounts…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {accountsData?.pagination && accountsData.pagination.pages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Page {accountsData.pagination.page} of {accountsData.pagination.pages}
                {' '}({accountsData.pagination.total} accounts)
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1 text-sm rounded-lg border disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  disabled={page >= accountsData.pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1 text-sm rounded-lg border disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowbackHealthTab;
